import DeleteIcon from '@assets/icons/delete.svg';
import React, { useMemo } from 'react';
import { CONTENT_MARGIN_VERTICAL } from './navigation-header-content';
import {
  Keyboard,
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {
  NavigationHeader,
  Props as NavigationHeaderProps,
} from './navigation-header';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useStatusBarColor } from '@/hooks/use-status-bar-color';
import { useTheme } from '@/contexts/theme-context';

export interface Props {
  children: React.ReactNode;
  navigationHeaderProps?: NavigationHeaderProps;
  shouldDismissKeyboard?: boolean;
  showLargeLayoutCloseButton?: boolean;
  transparentNavigationHeader?: boolean;
}

export const ModalContainer: React.FC<Props> = ({
  children,
  navigationHeaderProps: baseNavigationHeaderProps = {},
  shouldDismissKeyboard,
  showLargeLayoutCloseButton = true,
  transparentNavigationHeader = false,
}) => {
  const theme = useTheme();

  const { goBack } = useNavigation();
  const { isSmallLayout } = useLayoutContext();

  useStatusBarColor(theme.colors.white);

  const navigationHeaderProps = useMemo(() => {
    const { actions, containerStyle, ...rest } = baseNavigationHeaderProps;
    return {
      actions:
        isSmallLayout || !showLargeLayoutCloseButton
          ? actions
          : (actions || []).concat({
              Icon: DeleteIcon,
              onPress: goBack,
              testID: 'close-icon',
            }),
      BackButtonIcon: DeleteIcon,
      centerTitle: true,
      // NavigationHeader applies safe area padding so this reduces it for iOS
      containerStyle: [
        Platform.select({ ios: { paddingTop: 8 } }),
        ...(transparentNavigationHeader
          ? [{ backgroundColor: theme.colors.transparent }]
          : []),
        containerStyle,
      ],
      contentStyle: isSmallLayout ? null : styles.headerContentLarge,
      showBackButton: isSmallLayout,
      showShadow: false,
      ...rest,
    };
  }, [
    baseNavigationHeaderProps,
    goBack,
    isSmallLayout,
    showLargeLayoutCloseButton,
    theme.colors.transparent,
    transparentNavigationHeader,
  ]);

  const content = (
    <View
      style={transparentNavigationHeader ? styles.baseTransparent : styles.base}
    >
      <View
        style={[
          transparentNavigationHeader
            ? StyleSheet.absoluteFill
            : styles.content,
        ]}
      >
        {children}
      </View>
      <NavigationHeader
        {...navigationHeaderProps}
        contentContainerStyle={
          transparentNavigationHeader
            ? styles.navigationHeaderTransparent
            : null
        }
      />
    </View>
  );

  return shouldDismissKeyboard && Platform.OS !== 'web' ? (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      {content}
    </TouchableWithoutFeedback>
  ) : (
    content
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
    flexDirection: 'column-reverse',
  },
  baseTransparent: {
    flex: 1,
    position: 'relative',
  },
  content: {
    flex: 1,
  },
  headerContentLarge: {
    marginTop: CONTENT_MARGIN_VERTICAL,
  },
  navigationHeaderTransparent: {
    ...Platform.select({
      ios: {
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
      },
    }),
  },
});
